import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../components/LoadingSpinner";

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("");
  const [accountData, setAccountData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        // Extract parameters from URL
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const error = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");
        const platform = searchParams.get("platform") || "unknown";

        // Check for OAuth errors
        if (error) {
          throw new Error(errorDescription || `OAuth error: ${error}`);
        }

        if (!code || !state) {
          throw new Error("Missing required OAuth parameters");
        }

        setMessage(`Connecting your ${platform} account...`);

        // Exchange code for tokens
        const response = await fetch("/api/oauth/callback", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            code,
            state,
            platform,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          setAccountData(data.account);
          setStatus("success");
          setMessage(`Successfully connected your ${platform} account!`);

          // Send success message to parent window
          if (window.opener) {
            window.opener.postMessage(
              {
                type: "oauth_success",
                account: data.account,
                platform,
              },
              window.location.origin,
            );
          }

          // Close popup after delay
          setTimeout(() => {
            if (window.opener) {
              window.close();
            } else {
              // If not in popup, redirect to social accounts page
              window.location.href = "/social-accounts";
            }
          }, 2000);
        } else {
          throw new Error(data.error || "Failed to process OAuth callback");
        }
      } catch (err) {
        console.error("OAuth callback error:", err);
        setError(err.message);
        setStatus("error");
        setMessage("Failed to connect account");

        // Send error message to parent window
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "oauth_error",
              error: err.message,
            },
            window.location.origin,
          );

          // Close popup after delay
          setTimeout(() => {
            window.close();
          }, 3000);
        }
      }
    };

    processOAuthCallback();
  }, [searchParams]);

  const renderProcessing = () => (
    <div className="text-center space-y-6">
      <div className="animate-spin mx-auto">
        <ArrowPathIcon className="h-12 w-12 text-primary-600" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Processing Connection
        </h2>
        <p className="text-gray-600">{message}</p>
      </div>
      <LoadingSpinner size="lg" />
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center space-y-6">
      <div className="animate-bounce">
        <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Connection Successful!
        </h2>
        <p className="text-gray-600 mb-4">{message}</p>

        {accountData && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-center justify-center space-x-3">
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  accountData.platform === "facebook"
                    ? "bg-facebook"
                    : accountData.platform === "instagram"
                      ? "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500"
                      : accountData.platform === "twitter"
                        ? "bg-black"
                        : accountData.platform === "linkedin"
                          ? "bg-linkedin"
                          : accountData.platform === "tiktok"
                            ? "bg-black"
                            : accountData.platform === "threads"
                              ? "bg-black"
                              : "bg-gray-500"
                }`}
              >
                <span className="text-white text-lg">
                  {accountData.platform === "facebook"
                    ? "👥"
                    : accountData.platform === "instagram"
                      ? "📷"
                      : accountData.platform === "twitter"
                        ? "🐦"
                        : accountData.platform === "linkedin"
                          ? "💼"
                          : accountData.platform === "tiktok"
                            ? "🎵"
                            : accountData.platform === "threads"
                              ? "🧵"
                              : "📱"}
                </span>
              </div>
              <div className="text-left">
                <div className="font-medium text-gray-900">
                  {accountData.display_name || accountData.username}
                </div>
                <div className="text-sm text-gray-600">
                  @{accountData.username}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className={`border rounded-lg p-4 max-w-md mx-auto ${
          searchParams.get("platform") === "instagram"
            ? "bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200"
            : "bg-blue-50 border-blue-200"
        }`}
      >
        <h3
          className={`text-sm font-medium mb-2 ${
            searchParams.get("platform") === "instagram"
              ? "text-purple-800"
              : "text-blue-800"
          }`}
        >
          What's Next?
        </h3>
        <ul
          className={`text-sm space-y-1 text-left ${
            searchParams.get("platform") === "instagram"
              ? "text-purple-700"
              : "text-blue-700"
          }`}
        >
          <li className="flex items-center">
            <div
              className={`w-1 h-1 rounded-full mr-2 mt-2 flex-shrink-0 ${
                searchParams.get("platform") === "instagram"
                  ? "bg-purple-600"
                  : "bg-blue-600"
              }`}
            />
            This window will close automatically
          </li>
          <li className="flex items-center">
            <div
              className={`w-1 h-1 rounded-full mr-2 mt-2 flex-shrink-0 ${
                searchParams.get("platform") === "instagram"
                  ? "bg-purple-600"
                  : "bg-blue-600"
              }`}
            />
            {searchParams.get("platform") === "instagram"
              ? "Instagram Business data will start syncing"
              : "Your account will start syncing data immediately"}
          </li>
          <li className="flex items-center">
            <div
              className={`w-1 h-1 rounded-full mr-2 mt-2 flex-shrink-0 ${
                searchParams.get("platform") === "instagram"
                  ? "bg-purple-600"
                  : "bg-blue-600"
              }`}
            />
            {searchParams.get("platform") === "instagram"
              ? "Stories, Reels, and insights will be available hourly"
              : "View metrics in your dashboard within minutes"}
          </li>
        </ul>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="text-center space-y-6">
      <div className="animate-pulse">
        <XCircleIcon className="h-16 w-16 text-red-500 mx-auto" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Connection Failed
        </h2>
        <p className="text-gray-600 mb-4">{message}</p>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <h3 className="text-sm font-medium text-red-800 mb-1">
                  Error Details
                </h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-w-md mx-auto">
        <h3 className="text-sm font-medium text-gray-900 mb-2">
          Common Solutions
        </h3>
        <ul className="text-sm text-gray-700 space-y-1 text-left">
          <li className="flex items-center">
            <div className="w-1 h-1 bg-gray-600 rounded-full mr-2 mt-2 flex-shrink-0" />
            Ensure you're logged into the social platform
          </li>
          <li className="flex items-center">
            <div className="w-1 h-1 bg-gray-600 rounded-full mr-2 mt-2 flex-shrink-0" />
            Check if you granted all required permissions
          </li>
          <li className="flex items-center">
            <div className="w-1 h-1 bg-gray-600 rounded-full mr-2 mt-2 flex-shrink-0" />
            Try connecting again with an incognito window
          </li>
          <li className="flex items-center">
            <div className="w-1 h-1 bg-gray-600 rounded-full mr-2 mt-2 flex-shrink-0" />
            Contact support if the issue persists
          </li>
        </ul>
      </div>

      <div className="flex justify-center space-x-4">
        <button
          onClick={() => window.close()}
          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Close Window
        </button>
        <button
          onClick={() => (window.location.href = "/social-accounts")}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-2xl w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-lg font-bold">DZ</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">DZ Metrics</h1>
          <p className="text-gray-600 mt-1">Social Media Analytics</p>
        </div>

        {/* Content based on status */}
        {status === "processing" && renderProcessing()}
        {status === "success" && renderSuccess()}
        {status === "error" && renderError()}
      </div>
    </div>
  );
};

export default OAuthCallback;
